/**
 * Cookie Consent Service
 * GDPR & ePrivacy Directive compliant cookie consent management
 * 
 * This service can be used by Dependra users to implement cookie consent on their websites
 */

export type CookieCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export interface CookieConsent {
  essential: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string; // Policy version
}

export interface CookieConsentConfig {
  storageKey?: string;
  policyVersion?: string;
  defaultConsent?: Partial<Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>>;
  onConsentChange?: (consent: CookieConsent) => void;
}

const DEFAULT_CONFIG: Required<CookieConsentConfig> = {
  storageKey: 'cookie_consent',
  policyVersion: '1.0.0',
  defaultConsent: {
    functional: false,
    analytics: false,
    marketing: false,
  },
  onConsentChange: () => {},
};

class CookieConsentService {
  private readonly config: Required<CookieConsentConfig>;

  constructor(config: CookieConsentConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Get current consent preferences
   */
  getConsent(): CookieConsent | null {
    if (globalThis.window === undefined) return null;

    try {
      const stored = localStorage.getItem(this.config.storageKey);
      if (!stored) return null;

      const consent = JSON.parse(stored) as CookieConsent;
      
      // Check if policy version has changed (user needs to re-consent)
      if (consent.version !== this.config.policyVersion) {
        return null;
      }

      return consent;
    } catch (error) {
      console.error('Error reading cookie consent:', error);
      return null;
    }
  }

  /**
   * Save consent preferences
   */
  saveConsent(consent: Omit<CookieConsent, 'essential' | 'timestamp' | 'version'>): void {
    if (globalThis.window === undefined) return;

    const fullConsent: CookieConsent = {
      essential: true, // Always true
      ...consent,
      timestamp: Date.now(),
      version: this.config.policyVersion,
    };

    try {
      localStorage.setItem(this.config.storageKey, JSON.stringify(fullConsent));
      this.config.onConsentChange(fullConsent);
      
      // Clean up cookies that are no longer allowed
      this.enforceCookieConsent(fullConsent);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    this.saveConsent({
      functional: true,
      analytics: true,
      marketing: true,
    });
  }

  /**
   * Reject all non-essential cookies
   */
  rejectAll(): void {
    this.saveConsent({
      functional: false,
      analytics: false,
      marketing: false,
    });
  }

  /**
   * Check if user has given consent
   */
  hasConsent(): boolean {
    return this.getConsent() !== null;
  }

  /**
   * Check if specific category is allowed
   */
  isCategoryAllowed(category: CookieCategory): boolean {
    const consent = this.getConsent();
    if (!consent) return category === 'essential'; // Only essential allowed by default
    return consent[category] ?? false;
  }

  /**
   * Clear all consent data
   */
  clearConsent(): void {
    if (globalThis.window === undefined) return;
    localStorage.removeItem(this.config.storageKey);
  }

  /**
   * Enforce cookie consent by removing non-allowed cookies
   */
  private enforceCookieConsent(consent: CookieConsent): void {
    if (typeof document === 'undefined') return;

    const cookiesToRemove: { [key in CookieCategory]?: string[] } = {
      functional: ['user_preferences', 'notifications_seen'],
      analytics: ['_ga', '_gid', '_gat'],
      marketing: ['_fbp', '_fbc', '__utm'],
    };

    // Remove cookies for categories that are not consented
    (Object.keys(cookiesToRemove) as CookieCategory[]).forEach((category) => {
      const cookies = cookiesToRemove[category];
      if (!consent[category] && cookies) {
        cookies.forEach((cookieName) => {
          this.deleteCookie(cookieName);
        });
      }
    });
  }

  /**
   * Delete a specific cookie
   */
  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') return;
    
    // Delete for current domain
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    
    // Delete for parent domain
    const domain = globalThis.window.location.hostname;
    const parts = domain.split('.');
    if (parts.length > 1) {
      const parentDomain = parts.slice(-2).join('.');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${parentDomain}`;
    }
  }

  /**
   * Set a cookie with consent check
   */
  setCookie(
    name: string,
    value: string,
    category: CookieCategory,
    days: number = 365
  ): boolean {
    if (!this.isCategoryAllowed(category)) {
      console.warn(`Cookie "${name}" not set: ${category} cookies not allowed`);
      return false;
    }

    if (typeof document === 'undefined') return false;

    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
    return true;
  }

  /**
   * Get a cookie value
   */
  getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;

    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(nameEQ)) {
        return trimmed.substring(nameEQ.length);
      }
    }
    
    return null;
  }
}

// Export singleton instance
export const cookieConsent = new CookieConsentService();

// Export class for custom configurations
export default CookieConsentService;
